import React, { useState } from 'react';
import classnames from 'classnames';
import styles from './admission-section.module.scss';
import update from 'immutability-helper';
import { useDrop } from 'react-dnd';
import { CreateBox } from 'src/components/admin';
import { AdmissionCategory } from '../../admission-category';
import { Lang } from 'shared/constants/lang';
import { SectionComponent } from 'src/components/common/section/section.component';
import { useSectionOpenState, useViewSelector } from 'src/components/common/view-selector';
import { useSelector } from 'react-redux';
import { haveEditPermissions } from 'shared/store/selectors/knowledge.selector';
import { AdmissionCategoryMovePopup } from '../../admission-category/admission-category-move/admission-category-move';
import { GridItemsNavigator, ListItemsNavigator } from 'src/components/common/items-navigator';

const AdmissionSectionComponent = React.memo(function AdmissionSectionComponent({
  section = {
    id: '',
    title: '',
    fullPath: null,
  },
  children = [],
  adminMode = false,
  activeId = null,
  admissionSelectChecklist = () => {},
  deleteCategory = () => {},
  createCategory = () => {},
  updateCategory = () => {},
  editCategory = () => {},
  updateCategorySubcategories = () => {},
  firstItem,
  lastItem = false,
  changeSectionOrder = () => {},
  index = null,
  lang = Lang.ACTION_BUTTON_TOOLTIPS,
}) {
  let { id, title, fullPath, ownerIds, path, tags, keywords, type } = section;
  const [isMoveOpen, setMoveOpen] = useState(false);
  const hasEditPermission = useSelector(state => haveEditPermissions(state, { ownerIds }));

  // drag and drop items
  const dndGroup = `contentItems-${id}`;
  const [, drop] = useDrop({ accept: dndGroup });
  const [items, setItems] = React.useState(children);
  let isLoading = false;

  React.useEffect(() => {
    setItems(children);
  }, [children]);

  const dragItem = (id, atIndex) => {
    const { item, index } = findItem(id);
    setItems(
      update(items, {
        $splice: [
          [index, 1],
          [atIndex, 0, item],
        ],
      }),
    );
  };

  const findItem = id => {
    const item = items.find(i => `${i.id}` === id);
    return {
      item,
      index: items.indexOf(item),
    };
  };

  const finishDrag = categoryId => {
    updateCategorySubcategories({
      id,
      categoryId,
      index: findItem(categoryId).index,
    });
  };

  // Modification actions
  const deleteCategoryAction = React.useCallback(
    e => {
      e.stopPropagation();
      // TODO: Analytics
      deleteCategory({ id, title, path, type, ownerIds });
    },
    [deleteCategory, id, title, path, type, ownerIds],
  );

  // Modification actions
  const editCategoryAction = React.useCallback(
    e => {
      e.stopPropagation();
      // TODO: Analytics
      editCategory({ id, title, ownerIds, path, tags, keywords, type });
    },
    [editCategory, id, ownerIds, path, title, tags, keywords, type],
  );

  const moveCategoryAction = React.useCallback(
    e => {
      e.stopPropagation();

      if (!hasEditPermission) {
        return;
      }

      setMoveOpen(true);
    },
    [hasEditPermission],
  );

  const createNewCategoryAction = React.useCallback(() => {
    // TODO: Analytics
    createCategory({ path: fullPath, ownerIds, tags });
  }, [createCategory, fullPath, ownerIds, tags]);

  const { isGridView } = useViewSelector();

  const renderItems = () => {
    if (!items) return null;

    const categoryItems = items.map(item => (
      <AdmissionCategory
        key={`content-box-${item.id}`}
        item={item}
        {...{
          adminMode,
          activeId,
          admissionSelectChecklist,
          deleteCategory,
          updateCategory,
          editCategory,
          dragItem,
          findItem,
          dndGroup,
          finishDrag,
          lang,
        }}
      />
    ));

    if (isGridView) {
      return (
        <div ref={drop}>
          <GridItemsNavigator className={styles.items} dynamicColumns>
            {categoryItems}
            <CreateBox adminMode={adminMode} onClick={createNewCategoryAction} lang={lang} />
          </GridItemsNavigator>
        </div>
      );
    } else {
      return (
        <div ref={drop} className={styles.dropContainer}>
          <ListItemsNavigator className={styles.itemsColumn}>
            {categoryItems}
            <CreateBox adminMode={adminMode} onClick={createNewCategoryAction} lang={lang} />
          </ListItemsNavigator>
        </div>
      );
    }
  };

  const changeOrder = React.useCallback(
    async e => {
      e.stopPropagation();
      const { target } = e;

      if (isLoading) return false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      isLoading = true;

      const { dataset } = target;

      const toIndex = dataset.direction === 'up' ? index - 1 : index + 1;
      await changeSectionOrder(id, toIndex);
      isLoading = false;
    },
    [index, id],
  );

  const adminActions = [
    {
      label: lang.MOVE_UP,
      onClick: changeOrder,
      props: { 'data-direction': 'up', hidden: firstItem },
    },
    {
      label: lang.MOVE_DOWN,
      onClick: changeOrder,
      props: { 'data-direction': 'down', hidden: lastItem },
    },
    {
      label: lang.EDIT,
      onClick: editCategoryAction,
      tooltip: !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission,
    },
    {
      label: lang.MOVE,
      onClick: moveCategoryAction,
      tooltip: !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission,
    },
    {
      label: lang.DELETE,
      onClick: deleteCategoryAction,
      tooltip: !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission,
    },
  ];
  const onBlur = React.useCallback(
    e => {
      e.stopPropagation();
      const { textContent: value } = e.target;
      // TODO: add analytics

      if (value === title) return false;
      updateCategory({ id, title: value });
    },
    [id, title, updateCategory],
  );

  const { isOpen, onToggle } = useSectionOpenState({
    isFirst: firstItem,
    isContainsActiveBinder: items.some(item => item.id === activeId),
    id,
  });

  return (
    <section
      key={id}
      className={classnames(styles.root, adminMode ? styles.admin : {})}
      role="directory"
      aria-label={title}>
      <span className={styles.anchor} id={id} />
      <SectionComponent
        title={title}
        showActions={adminMode}
        onBlur={onBlur}
        adminActions={adminActions}
        isOpen={isOpen}
        onToggle={onToggle}>
        {renderItems()}
      </SectionComponent>
      <AdmissionCategoryMovePopup
        isOpen={isMoveOpen}
        categoryId={id}
        id={id}
        onFinish={() => setMoveOpen(false)}
      />
    </section>
  );
});

export { AdmissionSectionComponent };
